import type { HTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import styles from './Skeleton.module.css';

/**
 * Lifted from `@parrot/ui` (libs/ui/src/Skeleton) — see docs/AUDIT_FE_STANDARDS.md
 * for why a copy rather than a dependency.
 *
 * Unmodified except for the `cx` import path. The lift contract is that every
 * value stays a `var(--…)`: a lifted component keeps looking like Parrot only
 * because it resolves against `@parrot/tokens`, so never hardcode a colour,
 * space or radius in here.
 */

type Props = HTMLAttributes<HTMLSpanElement> & {
  shape?: 'rect' | 'text' | 'circle';
  /** Number means px. Defaults to filling the container. */
  width?: string | number;
  /** Number means px. `circle` derives its width from this. */
  height?: string | number;
  /** Off for long lists, where dozens of simultaneous shimmers cost more than they say. */
  animate?: boolean;
};

const toLength = (value: string | number) => (typeof value === 'number' ? `${value}px` : value);

/**
 * A placeholder for content that hasn't loaded.
 *
 * `aria-hidden`, because a screen reader has nothing to gain from the shape of
 * absent content — announce the wait on the container instead, with `aria-busy`
 * or a live region. A page of these otherwise reads as total silence.
 */
export const Skeleton = ({
  shape = 'rect',
  width,
  height,
  animate = true,
  className,
  style,
  ...spanProps
}: Props) => (
  <span
    aria-hidden={'true'}
    className={cx(styles.base, styles[shape], !animate && styles.static, className)}
    style={{
      ...(width !== undefined && { '--skeleton-width': toLength(width) }),
      ...(height !== undefined && { '--skeleton-height': toLength(height) }),
      ...style,
    }}
    {...spanProps}
  />
);
